import React, { useRef, useEffect } from 'react';
import classnames from 'classnames';
import Transition from '../util/Transition';
import { useSwipeableModal } from '../hooks/useSwipeableModal';
import { isMobileView } from '../util/device';

function ModalBlank({
  children,
  id,
  modalOpen,
  setModalOpen
}) {
  const { onTouchStart, onTouchMove, onTouchEnd, modalStyle } = useSwipeableModal(() => setModalOpen(false));
  const modalContent = useRef(null);

  // close on click outside
  useEffect(() => {
    if (!modalOpen) return;

    const clickHandler = ({ target }) => {
      if (modalContent.current && modalContent.current.contains(target)) return;
      setModalOpen(false);
    };

    // delay the attachment of the click handler to avoid capturing the initial click
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', clickHandler);
    }, 0); // 0ms delay pushes the handler to the end of the call stack

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', clickHandler);
    };
  }, [modalOpen, setModalOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!modalOpen || keyCode !== 27) return;
      setModalOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [modalOpen, setModalOpen]);

  return (
    <>
      {/* Modal backdrop */}
      <Transition
        className="fixed inset-0 z-50 transition-opacity bg-[#2B2E31]/[0.3] dark:bg-[#313233]/[0.7] mix-blend-multiply"
        show={modalOpen}
        enter="transition ease-out duration-200"
        enterStart="opacity-0"
        enterEnd="opacity-100"
        leave="transition ease-out duration-100"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        aria-hidden="true"
      />
      {/* Modal dialog */}
      <Transition
        id={id}
        className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center md:px-6 md:my-4"
        role="dialog"
        aria-modal="true"
        show={modalOpen}
        enter="transition ease-in-out duration-200"
        enterStart={`opacity-0 ${isMobileView() ? 'translate-y-full' : 'translate-y-4'}`}
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-in-out duration-200"
        leaveStart="opacity-100 translate-y-0"
        leaveEnd={`opacity-0 ${isMobileView() ? 'translate-y-full' : 'translate-y-4'}`}
      >
        <div
          ref={modalContent}
          className="bg-white dark:bg-[#242F3E] rounded-[12px_12px_0_0] md:rounded-[12px] shadow-[2px_4px_24px_0_rgba(4,13,22,0.30)] dark:shadow-[0px_0px_14px_0px_rgba(11,38,63,0.10)] overflow-auto md:max-w-[500px] w-full max-h-full mt-auto md:mt-0"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          style={modalStyle}
        >
          <div className={classnames(
            'px-4 py-6 md:p-8 md:pr-16 relative',
            isMobileView() ? '-with-swipe-pane' : '',
          )}>
            {children}
          </div>
        </div>
      </Transition>
    </>
  );
}

export default ModalBlank;
